import { DEFAULT_MAX_SEARCH_COUNT, MedplumClient, resolveId, WithId } from "@medplum/core";
import type { Flag } from "@medplum/fhirtypes";

import { setOrganizationAccountsOnResource } from "@/utils/accounts";
import { getOrCreateResource, joinReferenceString } from "@/utils/medplum";
import { isMedplumForbiddenError } from "@/utils/medplum-forbidden";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { FlagFailure } from "../constants/flag-failures";
import { ACTIVE_FLAG_STATUS } from "../constants/flags";
import { ClientFlag } from "../interfaces/client-flags";
import {
  buildClientFlagGuardQuery,
  buildClientFlagResource,
  getLibraryFlagId,
  sortClientFlagsByPriority,
  toClientFlag,
} from "../utils/client-flag";
import { toPracticeFlag } from "../utils/practice-flag";

import {
  loadPracticeLibrary,
  resolveLibraryGroupReference,
  searchLibraryFlagsByIds,
} from "./practice-flag-access";

/**
 * Reading the library flags applied to a client's chart.
 *
 * An applied flag carries a reference to its library entry and nothing else — see
 * `@/flags/utils/client-flag`. Resolving a chart therefore takes two searches, one for the chart's
 * flags and one for the library entries they point at, joined in memory. The join is never a
 * per-row read: a chart with twenty flags costs the same two requests as one with two.
 *
 * The library half of that join includes deleted entries. A chart read is the one place that needs
 * them: a flag whose entry was deleted stays on the chart, marked deprecated, and still takes its
 * name and priority from that entry.
 */

/**
 * `LookupFailed` is the Group read, the library search, or the chart search. `Forbidden` is the only
 * reason here that is not a fault — it belongs to the read because only a read can be refused.
 */
export type GetClientFlagsFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.MissingLibraryGroup
  | FlagFailure.Forbidden;

export type GetClientFlagsResult =
  | { ok: true; data: ClientFlag[] }
  | { ok: false; reason: GetClientFlagsFailure };

/**
 * Every library flag applied to one client, ordered High → Medium → Low.
 *
 * Resolves against library entries whether or not they are deleted, so a flag whose entry has left
 * the library still appears — marked deprecated rather than dropped. A flag stays on a chart until a
 * provider removes it, which is what the library's delete confirmation promises.
 *
 * Never throws, mirroring `getPracticeFlags`: every step that can reject becomes a `reason`, so a
 * caller can drive the UI from the result alone.
 *
 * @param clientId - Patient logical id whose chart is being read
 * @param practiceId - Practice Organization logical id, which scopes the library
 * @returns `{ ok: true, data }` with a row per applied flag, or `{ ok: false, reason }`
 */
export async function getClientFlags({
  medplum,
  clientId,
  practiceId,
}: {
  medplum: MedplumClient;
  clientId: string;
  practiceId: string;
}): Promise<GetClientFlagsResult> {
  const loaded = await loadAppliedFlagsWithLibrary(medplum, practiceId, () =>
    searchClientFlags(medplum, [clientId]),
  );

  if (!loaded.ok) {
    return { ok: false, reason: loaded.reason };
  }

  return {
    ok: true,
    data: sortClientFlagsByPriority(toRows(loaded.appliedFlags, loaded.libraryFlags)),
  };
}

/**
 * The two halves of a chart read: the applied flags, and the library entries they resolve against.
 *
 * The single-chart read and the page read differ only in which search finds the applied flags, so
 * the caller hands that in and everything around it — the Group resolution, the library search,
 * and the forbidden branch — is settled once here.
 *
 * The library search needs the ids the chart search finds, so it runs second. The Group lookup runs
 * alongside the chart search, which keeps a chart read at two round trips. That is why this does
 * not use `loadPracticeLibrary`.
 *
 * @param searchAppliedFlags - Finds the chart flags to join, for one client or for a page of them
 */
async function loadAppliedFlagsWithLibrary(
  medplum: MedplumClient,
  practiceId: string,
  searchAppliedFlags: () => Promise<WithId<Flag>[]>,
): Promise<
  | { ok: true; appliedFlags: WithId<Flag>[]; libraryFlags: WithId<Flag>[] }
  | { ok: false; reason: GetClientFlagsFailure }
> {
  try {
    // `resolveLibraryGroupReference` never throws, so any rejection here comes from a search.
    const [libraryGroup, appliedFlags] = await Promise.all([
      resolveLibraryGroupReference<GetClientFlagsFailure>(medplum, practiceId, {
        lookupFailed: FlagFailure.LookupFailed,
        missingLibraryGroup: FlagFailure.MissingLibraryGroup,
      }),
      searchAppliedFlags(),
    ]);

    if (!libraryGroup.ok) {
      return { ok: false, reason: libraryGroup.reason };
    }

    const libraryFlagIds = appliedFlags.reduce<Set<string>>((ids, appliedFlag) => {
      const libraryFlagId = getLibraryFlagId(appliedFlag);

      return libraryFlagId ? ids.add(libraryFlagId) : ids;
    }, new Set());

    const libraryFlags = libraryFlagIds.size
      ? await searchLibraryFlagsByIds(medplum, libraryGroup.reference, [...libraryFlagIds])
      : [];

    return { ok: true, appliedFlags, libraryFlags };
  } catch (error) {
    if (isMedplumForbiddenError(error)) {
      return { ok: false, reason: FlagFailure.Forbidden };
    }

    captureExceptionWithoutPhi("Failed to read client flags and their library entries", error, {
      practiceId,
    });
    return { ok: false, reason: FlagFailure.LookupFailed };
  }
}

/**
 * Joins applied flags to their library entries.
 *
 * Shared by the single-chart read and the page read so both answer the same way about a flag whose
 * entry has been deleted: it is kept and marked deprecated, never dropped.
 *
 * A flag whose extension points at no entry at all is still dropped — there is nothing to render it
 * from. Deleting an entry deactivates it rather than erasing it, so that is a malformed resource
 * rather than the ordinary deleted case.
 *
 * @param libraryFlags - The library entries the applied flags reference, deleted ones included
 */
function toRows(appliedFlags: WithId<Flag>[], libraryFlags: WithId<Flag>[]): ClientFlag[] {
  const libraryEntries = new Map(libraryFlags.map((flag) => [flag.id, flag]));

  return appliedFlags.reduce<ClientFlag[]>((flags, appliedFlag) => {
    const libraryFlagId = getLibraryFlagId(appliedFlag);
    const libraryEntry = libraryFlagId ? libraryEntries.get(libraryFlagId) : undefined;

    if (!libraryEntry) return flags;

    flags.push(
      toClientFlag(
        appliedFlag,
        toPracticeFlag(libraryEntry),
        libraryEntry.status !== ACTIVE_FLAG_STATUS,
      ),
    );

    return flags;
  }, []);
}

/**
 * Every applied flag for a page of clients, keyed by Patient id and ordered within each client.
 *
 * Two searches for the whole page, not two per row: the client list renders twenty rows, and a
 * per-row read would put forty requests behind one page of the table. Clients with no flags are
 * absent from the map rather than present with an empty array, so a caller reads a missing entry
 * and an empty one the same way.
 *
 * Never throws. Every step that can reject becomes a `reason`.
 *
 * @param clientIds - Patient logical ids on the current page
 * @returns `{ ok: true, data }` keyed by Patient id, or `{ ok: false, reason }`
 */
export async function getClientFlagsForPatients({
  medplum,
  clientIds,
  practiceId,
}: {
  medplum: MedplumClient;
  clientIds: string[];
  practiceId: string;
}): Promise<
  { ok: true; data: Map<string, ClientFlag[]> } | { ok: false; reason: GetClientFlagsFailure }
> {
  if (!clientIds.length) return { ok: true, data: new Map() };

  const loaded = await loadAppliedFlagsWithLibrary(medplum, practiceId, () =>
    searchClientFlags(medplum, clientIds),
  );

  if (!loaded.ok) {
    return { ok: false, reason: loaded.reason };
  }

  const { appliedFlags, libraryFlags } = loaded;

  // Grouped before the join, not after: the join can still drop a malformed flag, so rows and
  // applied flags cannot be matched up by position once it has run.
  const flagsByClient = appliedFlags.reduce<Map<string, WithId<Flag>[]>>((grouped, flag) => {
    const clientId = resolveId(flag.subject);

    if (!clientId) return grouped;

    return grouped.set(clientId, [...(grouped.get(clientId) ?? []), flag]);
  }, new Map());

  return {
    ok: true,
    data: [...flagsByClient].reduce<Map<string, ClientFlag[]>>(
      (rowsByClient, [clientId, flags]) => {
        const rows = sortClientFlagsByPriority(toRows(flags, libraryFlags));

        // A client whose every flag is malformed reads as having none. A deleted library entry does
        // not land here — those flags are kept and counted, marked deprecated.
        return rows.length ? rowsByClient.set(clientId, rows) : rowsByClient;
      },
      new Map(),
    ),
  };
}

/**
 * The flags across one or more charts, in one search. `subject` takes a comma-separated list, which
 * FHIR reads as an OR, so a page of twenty charts costs the same request a single chart does.
 *
 * Reads every page of the result, so no chart is shown with one of its flags missing. A page holds
 * Medplum's maximum of 1000 flags, which a page of twenty charts stays well under.
 *
 * Uncached, so the info box reflects a save made moments ago rather than the bundle `MedplumClient`
 * held from before it.
 *
 * @param clientIds - Patient logical ids whose charts are being read, never empty
 * @param status - Which flags to read. Defaults to the active ones every chart surface shows; the
 *   flag archive asks for the inactive ones instead.
 * @param practiceId - Practice Organization logical id. When set, the search covers only that
 *   practice's compartment. When left out, the search has no compartment filter.
 */
export async function searchClientFlags(
  medplum: MedplumClient,
  clientIds: string[],
  {
    status = ACTIVE_FLAG_STATUS,
    practiceId,
  }: { status?: Flag["status"]; practiceId?: string } = {},
): Promise<WithId<Flag>[]> {
  const flags: WithId<Flag>[] = [];
  const pages = medplum.searchResourcePages(
    "Flag",
    {
      subject: clientIds.map((id) => joinReferenceString("Patient", id)).join(","),
      status,
      ...(practiceId && { _compartment: joinReferenceString("Organization", practiceId) }),
      _count: String(DEFAULT_MAX_SEARCH_COUNT),
    },
    { cache: "no-cache" },
  );

  for await (const page of pages) {
    flags.push(...page);
  }

  return flags;
}

/**
 * `LookupFailed` is the Group read or the library search. `NotFound` means one staged entry is gone
 * and nothing was written, whereas `WriteFailed` and `AccountsFailed` can both leave earlier flags
 * in place — the apply writes one at a time, so a rejection partway through is a partial save the
 * caller re-reads rather than a rollback.
 */
export type ApplyClientFlagsFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.MissingLibraryGroup
  | FlagFailure.NotFound
  | FlagFailure.WriteFailed
  | FlagFailure.AccountsFailed;

export type ApplyClientFlagsResult =
  | { ok: true; data: WithId<Flag>[] }
  | { ok: false; reason: ApplyClientFlagsFailure };

/**
 * Applies library entries to a client's chart.
 *
 * Every staged id is checked against the practice's active library before anything is written, so
 * a stale drawer fails the whole save rather than applying the half of it that still resolves.
 *
 * Each flag goes out as a conditional create guarded on the library id and the chart, which makes
 * the operation idempotent: a double-submit, or a flag the chart already carries, matches the guard
 * and writes nothing instead of adding a duplicate row.
 *
 * Writes are sequential rather than batched, because scoping a Flag to the practice compartment is
 * a second call Medplum only accepts once the resource exists. A rejection partway through leaves
 * the flags already written in place — they are valid, and the caller re-reads the chart, so the
 * user sees what landed rather than a silent partial.
 *
 * Never throws. Every step that can reject is captured into a `reason`.
 *
 * @param libraryFlagIds - Logical ids of the library entries to apply
 * @returns `{ ok: true, data }` with the applied Flags, or `{ ok: false, reason }`
 */
export async function applyClientFlags({
  medplum,
  clientId,
  practiceId,
  libraryFlagIds,
}: {
  medplum: MedplumClient;
  clientId: string;
  practiceId: string;
  libraryFlagIds: string[];
}): Promise<ApplyClientFlagsResult> {
  const library = await loadPracticeLibrary<ApplyClientFlagsFailure>(medplum, practiceId, {
    lookupFailed: FlagFailure.LookupFailed,
    missingLibraryGroup: FlagFailure.MissingLibraryGroup,
  });

  if (!library.ok) {
    return { ok: false, reason: library.reason };
  }

  const libraryEntries = new Map(library.flags.map((flag) => [flag.id, flag]));
  const entriesToApply = libraryFlagIds.map((id) => libraryEntries.get(id));

  if (entriesToApply.some((entry) => !entry)) {
    return { ok: false, reason: FlagFailure.NotFound };
  }

  const applied: WithId<Flag>[] = [];

  for (const entry of entriesToApply as WithId<Flag>[]) {
    let flag: WithId<Flag>;
    let created: boolean;
    try {
      [flag, created] = await getOrCreateResource(
        medplum,
        buildClientFlagResource(entry, clientId, new Date().toISOString()),
        buildClientFlagGuardQuery(entry.id, clientId),
      );
    } catch (error) {
      captureExceptionWithoutPhi("Failed to apply a library flag to a client chart", error, {
        clientId,
        libraryFlagId: entry.id,
      });
      return { ok: false, reason: FlagFailure.WriteFailed };
    }

    applied.push(flag);

    // The guard matched, so the chart already carries this flag and nothing was written — there is
    // no new resource to scope.
    if (!created) continue;

    // Medplum takes the accounts call only once the resource exists, so it follows the create. Its
    // own reason, because the Flag is written and valid — it is simply outside the practice
    // compartment — and saving again would not fix it.
    //
    // The Patient's accounts ride along so the flag is reachable by exactly the practitioners who
    // can reach the chart: an Organization-only account would leave it to the org-wide roles.
    try {
      await setOrganizationAccountsOnResource(medplum, flag, practiceId, {
        extractFromPatient: true,
      });
    } catch (error) {
      // The flag id is the only handle an operator has on a Flag the practice cannot see, so it
      // rides along with the report.
      captureExceptionWithoutPhi("Failed to set accounts on an applied client flag", error, {
        flagId: flag.id,
      });
      return { ok: false, reason: FlagFailure.AccountsFailed };
    }
  }

  medplum.invalidateSearches("Flag");

  return { ok: true, data: applied };
}
